import { Injectable } from '@angular/core';
import { SortDirection } from '@angular/material/sort';

import { LegFiJwtService } from './auth/legfi-jwt.service';
import { TableTypeNames } from './column.service';
import { LocalService } from './storage/local.service';
import { TableFilters } from '../models/entities/table-filters';

export interface StoredSortState {
    active: string;
    direction: Exclude<SortDirection, ''>;
}

type StoredFilterMap = Partial<Record<TableTypeNames, TableFilters>>;
type StoredSearchMap = Partial<Record<TableTypeNames, string>>;
type StoredSortMap = Partial<Record<TableTypeNames, StoredSortState>>;

/**
 * SD-7319 — Phase 1.
 *
 * Persists v2-layout `app-table` filter, search, and sort state to localStorage
 * so PMC Dashboard tables remember the user's last-applied state across
 * drill-in/back navigation, page switches, and hard reloads.
 *
 * Storage shape mirrors ColumnService:
 *   payhoa-filters-{orgId} -> { [tableType]: TableFilters }
 *   payhoa-search-{orgId}  -> { [tableType]: string }
 *   payhoa-sort-{orgId}    -> { [tableType]: StoredSortState }
 *
 * One localStorage entry per CONCEPT (not per concept × table), keyed by org —
 * matches ColumnService's `payhoa-columns-{orgId}` convention so a developer
 * inspecting DevTools sees the same shape for every persisted-table preference.
 *
 * Split by concept (filters / search / sort) instead of one combined blob,
 * matching the eventual backend-key shape established by SD-6481
 * (`filters.<tableId>`, `sort.<tableId>`), so the Phase-2 migration off
 * localStorage unrolls cleanly to per-table backend rows.
 *
 * SD-6481-followup: once SD-6481 lands the ColumnService backend migration via
 * the UserPreferences API, replace the localStorage read/write below with the
 * same backend client + debounced PUT pattern. Each concept-dict here becomes
 * one PUT per (user, org, table) row on the backend.
 */
@Injectable({
    providedIn: 'root',
})
export class FilterSortPersistenceService
{
    private static readonly FILTERS_DOMAIN = 'payhoa-filters';

    private static readonly SEARCH_DOMAIN = 'payhoa-search';

    private static readonly SORT_DOMAIN = 'payhoa-sort';

    readFilters(table: TableTypeNames): TableFilters | null {
        const all = this.readJson<StoredFilterMap>(this.scopedKey(FilterSortPersistenceService.FILTERS_DOMAIN));

        return all?.[table] ?? null;
    }

    writeFilters(table: TableTypeNames, filters: TableFilters): void {
        const all = this.readJson<StoredFilterMap>(this.scopedKey(FilterSortPersistenceService.FILTERS_DOMAIN)) ?? {};

        if (!filters || Object.keys(filters).length === 0) {
            delete all[table];
        } else {
            all[table] = filters;
        }

        this.writeMap(this.scopedKey(FilterSortPersistenceService.FILTERS_DOMAIN), all);
    }

    readSearch(table: TableTypeNames): string | null {
        const all = this.readJson<StoredSearchMap>(this.scopedKey(FilterSortPersistenceService.SEARCH_DOMAIN));
        const value = all?.[table];

        return value ? value : null;
    }

    writeSearch(table: TableTypeNames, value: string): void {
        const all = this.readJson<StoredSearchMap>(this.scopedKey(FilterSortPersistenceService.SEARCH_DOMAIN)) ?? {};

        if (value === null || value === undefined || value === '') {
            delete all[table];
        } else {
            all[table] = value;
        }

        this.writeMap(this.scopedKey(FilterSortPersistenceService.SEARCH_DOMAIN), all);
    }

    readSort(table: TableTypeNames): StoredSortState | null {
        const all = this.readJson<StoredSortMap>(this.scopedKey(FilterSortPersistenceService.SORT_DOMAIN));

        return all?.[table] ?? null;
    }

    writeSort(table: TableTypeNames, sort: StoredSortState | null): void {
        const all = this.readJson<StoredSortMap>(this.scopedKey(FilterSortPersistenceService.SORT_DOMAIN)) ?? {};

        if (!sort || !sort.active || (sort.direction !== 'asc' && sort.direction !== 'desc')) {
            delete all[table];
        } else {
            all[table] = sort;
        }

        this.writeMap(this.scopedKey(FilterSortPersistenceService.SORT_DOMAIN), all);
    }

    /**
     * Scope each concept-key by org so PMCs who manage multiple orgs don't
     * bleed state across the org switcher. Mirrors ColumnService's per-org key
     * convention exactly (`payhoa-columns-{orgId}` ↔ `payhoa-filters-{orgId}`).
     */
    private scopedKey(domain: string): string {
        const jwt = LegFiJwtService.read();
        const orgId = jwt?.orgId ?? 'anon';

        return `${domain}-${orgId}`;
    }

    private writeMap(key: string, map: Record<string, unknown>): void {
        if (Object.keys(map).length === 0) {
            LocalService.destroy(key);

            return;
        }

        LocalService.set(key, JSON.stringify(map));
    }

    private readJson<U>(key: string): U | null {
        const raw = LocalService.get(key);

        if (raw === null || raw === '') {
            return null;
        }

        try {
            return JSON.parse(raw) as U;
        } catch {
            // Corrupt entry — drop it so we don't trip on it again.
            LocalService.destroy(key);

            return null;
        }
    }
}
